import { MultiSelectInputField, TextareaInputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { useRequestRecalculationMutationHook } from "@/hooks";
import { useTenderPermissionsData } from "@/hooks/cache";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";

const createRequestRecalculationSchema = (t: TFunction) => z.object({
  operationalOrgIds: z.array(z.number()).min(1, {
    message: t("schemas:form.required")
  }),
  reason: z.string().min(1, {
    message: t("schemas:form.required")
  })
});

interface RequestRecalculationComponentProps {
  tenderID: string | undefined;
};


function RequestRecalculationComponent({ tenderID }: RequestRecalculationComponentProps) {
  const { t } = useTranslation(["components"]);
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const { requestRecalculationMutation, loading } = useRequestRecalculationMutationHook(tenderID);
  const { canRequestRecalculation } = useTenderPermissionsData(Number(tenderID));

  const marginDecisionGroups = tenderByIdData?.fetchTenderById?.marginDecisionGroups;

  const operationalOrganizations = marginDecisionGroups?.map((group) => {
    if (!group.operationalOrganizationId) return null;
    return {
      value: group.operationalOrganizationId,
      label: group.operationalOrganizationName ?? ""
    };
  }).filter((org): org is NonNullable<typeof org> => org !== null) ?? [];

  const requestRecalculationSchema = createRequestRecalculationSchema(t);

  const form = useForm<z.infer<typeof requestRecalculationSchema>>({
    resolver: zodResolver(requestRecalculationSchema),
    defaultValues: {
      operationalOrgIds: [],
      reason: ""
    },
    mode: "onChange",
  });

  const handleOnRequestRecalculation = (data: z.infer<typeof requestRecalculationSchema>) => {
    requestRecalculationMutation({
      variables: {
        tenderId: Number(tenderID),
        operationalOrgIds: data.operationalOrgIds,
        reason: data.reason
      }
    }).then(() => {
      form.reset();
    });
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">{t("tendersubmission.requestrecalculation.title")}</CardTitle>
          <CardDescription>{t("tendersubmission.requestrecalculation.descriptionone")}</CardDescription>
          <CardDescription>{t("tendersubmission.requestrecalculation.descriptiontwo")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleOnRequestRecalculation)} className="flex-col-4">
              <MultiSelectInputField
                control={form.control}
                name="operationalOrgIds"
                label={t("tendersubmission.requestrecalculation.inputs.operationalOrgIds.label")}
                placeholder={t("tendersubmission.requestrecalculation.inputs.operationalOrgIds.placeholder")}
                loading={false}
                options={operationalOrganizations}
                className="form-item"
                disabled={!canRequestRecalculation}
              />

              <TextareaInputField
                form={form}
                name="reason"
                label={t("tendersubmission.requestrecalculation.inputs.reason.label")}
                placeholder={t("tendersubmission.requestrecalculation.inputs.reason.placeholder")}
                className="w-full"
                isFormItem={false}
                disabled={!canRequestRecalculation}
              />

              <div className="flex justify-end">
                <Button disabled={loading || !canRequestRecalculation} type="submit" className="gap-2">
                  <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                  {t("tendersubmission.requestrecalculation.btnrecalculate")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

export default RequestRecalculationComponent;
